/**
 * 3D Visualizer — Debug Registry
 *
 * Tracks every mounted visualizer in the app so external tooling (snapshot
 * tests, screenshot capture, debug overlays) can introspect them without
 * prop-drilling refs. In dev builds the registry is exposed on
 * `window.__VISUALIZER_DEBUG__`. In production the global is not attached
 * (the registry itself still exists but has no external surface).
 */

import * as THREE from 'three';

export type VisualizerKind = 'room' | 'wall' | 'ceiling';

export interface VisualizerDebugEntry {
  /** Stable id assigned per mount. Use viewer prop hints to make it useful. */
  id: string;
  kind: VisualizerKind;
  renderer: THREE.WebGLRenderer;
  scene: THREE.Scene;
  camera: THREE.PerspectiveCamera;
  roomGroup: THREE.Group;
  /** Forces a render — useful before capturing a snapshot. */
  renderScene: () => void;
  /** The render pipeline, when the viewer draws through one (3D Designer). */
  pipeline?: unknown;
}

const entries = new Set<VisualizerDebugEntry>();
let nextAutoId = 0;

export function generateVisualizerId(kind: VisualizerKind, hint?: string): string {
  nextAutoId += 1;
  return hint ? `${kind}:${hint}:${nextAutoId}` : `${kind}:${nextAutoId}`;
}

export function registerVisualizer(entry: VisualizerDebugEntry): void {
  entries.add(entry);
}

export function unregisterVisualizer(entry: VisualizerDebugEntry): void {
  entries.delete(entry);
}

export function listVisualizers(): VisualizerDebugEntry[] {
  return Array.from(entries);
}

export function captureVisualizerPNG(id: string): string | null {
  const entry = listVisualizers().find((e) => e.id === id);
  if (!entry) return null;
  entry.renderScene();
  return entry.renderer.domElement.toDataURL('image/png');
}

if (process.env.NODE_ENV !== 'production' && typeof window !== 'undefined') {
  (window as unknown as { __VISUALIZER_DEBUG__: unknown }).__VISUALIZER_DEBUG__ = {
    list: listVisualizers,
    capture: captureVisualizerPNG,
  };
}
